import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import storageService from '../../../shared/storage/storageService';
import { downloadAsJSON, readJSONFile } from '../../../shared/utils/exportHelpers';
import { getStarterDeck } from '../data/starterDeck';

/**
 * Deck management: import/export JSON, reset to defaults, deck stats.
 */
export default function DeckManager({ cards, setCards }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [importResult, setImportResult] = useState(null);

  const handleExport = () => {
    downloadAsJSON(cards, `ccnp-flashcards-${new Date().toISOString().split('T')[0]}.json`);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await readJSONFile(file);
      const importedCards = Array.isArray(data) ? data : data.data?.all || data.cards || [];

      if (importedCards.length === 0) {
        setImportResult({ success: false, message: 'No cards found in file.' });
        return;
      }

      // Merge: add cards with new IDs that don't already exist
      const existingIds = new Set(cards.map(c => c.id));
      const newCards = importedCards.filter(c => !existingIds.has(c.id));
      const merged = [...cards, ...newCards];

      setCards(merged);
      setImportResult({
        success: true,
        message: `Imported ${newCards.length} new cards (${importedCards.length - newCards.length} duplicates skipped).`,
      });
    } catch (err) {
      setImportResult({ success: false, message: err.message });
    }

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleResetToDefaults = () => {
    if (window.confirm('This will replace ALL your flashcards with the original starter deck. Your custom cards and progress will be lost. Continue?')) {
      const starterDeck = getStarterDeck();
      setCards(starterDeck);
      storageService.resetInitialized('flashcards');
      storageService.markInitialized('flashcards');
      setImportResult({ success: true, message: `Reset to starter deck (${starterDeck.length} cards).` });
    }
  };

  // Stats
  const totalCards = cards.length;
  const seenCards = cards.filter(c => c.stats.times_seen > 0).length;
  const starterCards = cards.filter(c => c.source === 'starter').length;
  const userCards = cards.filter(c => c.source === 'user').length;
  const importedCards = cards.filter(c => ['imported', 'exam-export', 'matrix-export'].includes(c.source)).length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 page-enter">
      <h2 className="text-xl font-bold text-white mb-6">Deck Manager</h2>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatBox label="Total Cards" value={totalCards} />
        <StatBox label="Studied" value={seenCards} />
        <StatBox label="Starter" value={starterCards} />
        <StatBox label="Custom" value={userCards + importedCards} />
      </div>

      {/* Export */}
      <div className="card-gradient p-6 mb-4">
        <h3 className="text-white font-semibold mb-2">Export Deck</h3>
        <p className="text-gray-400 text-sm mb-4">
          Download all cards as a JSON file for backup or transfer to another device.
        </p>
        <button
          onClick={handleExport}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl
            hover:from-blue-500 hover:to-blue-400 text-sm font-medium transition-all duration-200"
        >
          Export as JSON
        </button>
      </div>

      {/* Import */}
      <div className="card-gradient p-6 mb-4">
        <h3 className="text-white font-semibold mb-2">Import Cards</h3>
        <p className="text-gray-400 text-sm mb-4">
          Import cards from a JSON file. Duplicate cards (same ID) will be skipped.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleImport}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="px-5 py-2.5 bg-gray-700/80 text-gray-300 rounded-xl hover:bg-gray-600 text-sm font-medium transition-colors"
        >
          Choose JSON File
        </button>
      </div>

      {/* Import result */}
      {importResult && (
        <div className={`rounded-xl p-4 mb-4 text-sm font-medium ${
          importResult.success
            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
            : 'bg-red-500/10 text-red-400 border border-red-500/20'
        }`}>
          {importResult.message}
        </div>
      )}

      {/* Reset */}
      <div className="bg-gray-800/80 rounded-xl border border-red-500/20 p-6 mb-6">
        <h3 className="text-red-400 font-semibold mb-2">Reset to Defaults</h3>
        <p className="text-gray-400 text-sm mb-4">
          Replace all cards with the original starter deck. This cannot be undone.
        </p>
        <button
          onClick={handleResetToDefaults}
          className="px-5 py-2.5 bg-red-900/40 text-red-400 rounded-xl hover:bg-red-900/70 text-sm font-medium transition-colors"
        >
          Reset Deck
        </button>
      </div>

      <button
        onClick={() => navigate('/flashcards')}
        className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
      >
        {'\u2190'} Back to Dashboard
      </button>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="card-gradient p-4 text-center">
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-[11px] text-gray-400 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}
